'use client';

import { type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import { useTranslations } from '@fshr/context/i18n-context';
import BevSafeText from '../bev-safe-text';
import { availabilityLabel, type AvailabilityTone } from './availability';
import type { AvailabilityState } from './types';

const DOT: Record<AvailabilityTone, string> = {
	available: 'bg-green-400',
	limited: 'bg-yellow-400',
	unavailable: 'bg-red-400',
};

type Props = {
	state: AvailabilityState;
	className?: string;
};

export default function AvailabilityIndicator({ state, className }: Props): ReactElement {
	const t = useTranslations();
	const { label, tone } = availabilityLabel(state);
	return (
		<div
			className={cn(
				// Figma's "availability indicator" itemSpacing is a flat 8px at
				// every tier, not 6 - px-040/py-060 were already exact matches.
				'flex w-full items-center justify-center gap-080 bg-neutral-1700/60 px-040 py-060 backdrop-blur-[8px]',
				className
			)}
		>
			<span className={cn('h-080 w-080 shrink-0 rounded-full', DOT[tone])} />
			<p
				className="text-caption min-w-0 translate-y-[1px] truncate text-text-on-image-primary"
				style={{ lineHeight: 1 }}
			>
				<BevSafeText>{t(label)}</BevSafeText>
			</p>
		</div>
	);
}
